// src/components/VoiceInput.jsx
import React, { useState, useRef } from 'react';
import { toast } from 'react-toastify';
import { convertSpeechToText } from '../utils/apiHandler';

const VoiceInput = ({ onTranscribe, getIdTokenClaims, getAccessTokenWithPopup, logout, disabled }) => {
    const [isRecording, setIsRecording] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const mediaRecorderRef = useRef(null);
    const streamRef = useRef(null);
    const audioChunksRef = useRef([]);

    const startRecording = async () => {
        if (disabled || isProcessing) return;

        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;

            const mediaRecorder = new MediaRecorder(stream);
            mediaRecorderRef.current = mediaRecorder;

            audioChunksRef.current = [];
            mediaRecorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    audioChunksRef.current.push(event.data);
                }
            };

            mediaRecorder.start();
            setIsRecording(true);
        } catch (error) {
            console.error('Error accessing microphone:', error);
            toast.error('Could not access microphone. Please check permissions.');
        }
    };

    const stopRecording = async () => {
        if (!isRecording || !mediaRecorderRef.current) return;

        setIsRecording(false);
        setIsProcessing(true);

        mediaRecorderRef.current.stop();

        // Wait for all data to be collected
        mediaRecorderRef.current.onstop = async () => {
            try {
                // Stop all tracks
                if (streamRef.current) {
                    streamRef.current.getTracks().forEach(track => track.stop());
                }

                // Create audio blob
                const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/wav' });

                // Convert to base64
                const reader = new FileReader();
                reader.onload = async () => {
                    const base64Audio = reader.result.split(',')[1]; // Remove the data URL prefix

                    // Send to STT API
                    const result = await convertSpeechToText(
                        getIdTokenClaims,
                        getAccessTokenWithPopup,
                        logout,
                        base64Audio
                    );

                    if (result && result.transcript) {
                        // Check confidence level
                        if (result.confidence >= 0.9) {
                            // Call the callback with the transcript - will auto-send
                            onTranscribe(result.transcript);
                        } else {
                            // Low confidence - let user know but still show the transcript
                            toast.warn('Voice recognition uncertain. Please verify the text.');
                            onTranscribe(result.transcript);
                        }
                    } else {
                        toast.error('Could not transcribe audio. Please try again.');
                    }

                    setIsProcessing(false);
                };

                reader.onerror = () => {
                    setIsProcessing(false);
                    toast.error('Error processing audio. Please try again.');
                };

                reader.readAsDataURL(audioBlob);
            } catch (error) {
                console.error('Error processing recording:', error);
                setIsProcessing(false);
                toast.error('Error processing audio. Please try again.');
            }
        };
    };

    // Clean up resources if component unmounts while recording
    React.useEffect(() => {
        return () => {
            if (streamRef.current) {
                streamRef.current.getTracks().forEach(track => track.stop());
            }
        };
    }, []);

    // Determine the button color based on state
    const getButtonColorClass = () => {
        if (disabled || isProcessing) {
            return 'bg-gray-300 text-gray-500'; // Grey when inactive
        } else if (isRecording) {
            return 'bg-red-200 text-red-600'; // Light green on recording
        } else {
            return 'bg-green-200 text-green-500'; // Light red on idle
        }
    };

    return (
        <button
            type="button"
            className={`voice-input-button p-2 rounded-full focus:outline-none transition-colors ${getButtonColorClass()} ${
                disabled || isProcessing ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-80'
            }`}
            onMouseDown={startRecording}
            onMouseUp={stopRecording}
            onMouseLeave={stopRecording}
            onTouchStart={startRecording}
            onTouchEnd={stopRecording}
            disabled={disabled || isProcessing}
            aria-label={isRecording ? 'Stop recording' : 'Start recording'}
        >
            {isProcessing ? (
                // Loading spinner icon (Tailwind Heroicons)
                <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
            ) : (
                // Microphone icon (Tailwind Heroicons)
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className={`h-5 w-5 ${isRecording ? 'recording' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
                    />
                </svg>
            )}
        </button>
    );
};

export default VoiceInput;